import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { convertCurrency, formatCurrency } from '../../utils/currencyUtils';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';

const ChartContainer = styled.div`
  background: linear-gradient(145deg, #ffffff, #f5f6fa);
  border-radius: 14px;
  padding: 20px;
  margin-top: 20px;
  box-shadow: 4px 4px 8px rgba(0, 0, 0, 0.05),
              -4px -4px 8px rgba(255, 255, 255, 0.8);
`;

const ChartTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #4a6fa1;
  margin: 0 0 20px 0;
  text-align: center;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 0;
  color: #8c96a5;
  
  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 10px;
    fill: #d1d9e6;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px 0;
  color: #8c96a5;
  
  svg {
    width: 24px;
    height: 24px;
    margin-bottom: 8px;
    fill: #4a6fa1;
    animation: rotate 1.5s linear infinite;
  }
  
  @keyframes rotate {
    100% {
      transform: rotate(360deg);
    }
  }
`;

const StatsContainer = styled.div`
  display: flex;
  justify-content: space-around;
  margin-top: 20px;
  flex-wrap: wrap;
  gap: 15px;
`;

const StatItem = styled.div`
  text-align: center;
  padding: 10px;
  min-width: 120px;
  background: linear-gradient(145deg, #f0f3f9, #e6ebf5);
  border-radius: 10px;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.05),
              -2px -2px 5px rgba(255, 255, 255, 0.8);
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 3px 3px 7px rgba(0, 0, 0, 0.07),
                -3px -3px 7px rgba(255, 255, 255, 0.9);
  }
  
  .stat-label {
    font-size: 13px;
    color: #6c7d95;
    margin-bottom: 5px;
  }
  
  .stat-value {
    font-size: 18px;
    font-weight: 600;
    color: ${props => props.$color || '#4a6fa1'};
  }
`;

// Иконка загрузки
const LoadingIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
    <path d="M12 4V2A10 10 0 0 0 2 12h2a8 8 0 0 1 8-8z"/>
  </svg>
);

// Иконка "Нет данных"
const NoDataIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-9 14h-2v-2h2v2zm0-4h-2V7h2v6zm4 4h-2v-4h2v4zm0-6h-2V7h2v4zm4 6h-2v-6h2v6zm0-8h-2V7h2v2z"/>
  </svg>
);

// Цвета для гистограммы
const EXPENSE_COLOR = '#FF6B6B';
const INCOME_COLOR = '#4ECDC4';
const AVERAGE_COLOR = '#FFD166';
const EMPTY_COLOR = '#E0E0E0';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ 
        backgroundColor: 'white', 
        padding: '12px', 
        border: '1px solid #ddd',
        borderRadius: '6px',
        boxShadow: '0px 2px 6px rgba(0,0,0,0.1)'
      }}>
        <p style={{ margin: 0, fontWeight: 'bold', color: '#4A6FA1' }}>{`День ${label}`}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ 
            margin: '5px 0 0', 
            fontSize: '14px',
            color: entry.color
          }}>
            {`${entry.name}: ${entry.formattedValue || entry.value}`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Функция для получения последнего дня месяца
const getDaysInMonth = (month, year = new Date().getFullYear()) => {
  return new Date(year, month, 0).getDate();
};

// Функция для извлечения дня из даты транзакций
const extractDayFromDate = (dateString) => {
  if (!dateString) return new Date().getDate();
  
  try {
    // Разбиваем строку на дату и время
    const parts = dateString.split(' ');
    const datePart = parts[0];
    
    // Проверяем формат даты
    if (datePart.includes('-')) {
      // Формат 'YYYY-MM-DD'
      const dateParts = datePart.split('-');
      if (dateParts.length === 3) {
        return parseInt(dateParts[2], 10);
      }
    } else if (datePart.includes('.')) {
      // Формат 'DD.MM.YYYY'
      const dateParts = datePart.split('.');
      if (dateParts.length >= 2) {
        return parseInt(dateParts[0], 10);
      }
    } else {
      // Пробуем создать объект Date
      const dateObj = new Date(dateString);
      if (!isNaN(dateObj.getTime())) {
        return dateObj.getDate();
      }
    }
    
    // Если не удалось определить день, возвращаем текущий
    return new Date().getDate();
  } catch (error) {
    console.error('Error extracting day from date:', error, dateString);
    return new Date().getDate();
  }
};

const ChartComponent = ({ 
  transactions, 
  exchangeRates, 
  preferredCurrency, 
  isLoading,
  month,
  type,
  formatCurrency
}) => {
  const { t } = useTranslation();
  const isSmallScreen = window.innerWidth <= 768;
   console.log('ChartComponent: Using currency:', preferredCurrency);
  console.log('ChartComponent: Has formatCurrency function:', !!formatCurrency);
  console.log('ChartComponent received transactions:', transactions?.length || 0);
  if (transactions && transactions.length > 0) {
    console.log('Sample transaction:', transactions[0]);
    console.log('Transaction date format:', transactions[0].date);
  }
  const safeFormatCurrency = typeof formatCurrency === 'function' 
    ? formatCurrency 
    : (amount, currency) => {
        // Упрощенная резервная функция
        if (!amount) return '0.00';
        const value = Number(amount).toFixed(2);
        const symbol = {
          'UAH': '₴', 'USD': '$', 'EUR': '€', 'INR': '₹', 'RUB': '₽'
        }[currency] || currency;
        
        // Для определенных валют размещаем символ после числа
        if (['UAH', 'RUB'].includes(currency)) {
          return `${value} ${symbol}`;
        } else {
          return `${symbol}${value}`;
        }
      };
  // Обработка данных для гистограммы
  const { 
    dailyData, 
    totalAmount, 
    averageAmount, 
    maxDailyAmount,
    maxDay,
    daysWithData
  } = useMemo(() => {
    if (!transactions || transactions.length === 0 || !month) {
      return { 
        dailyData: [], 
        totalAmount: 0, 
        averageAmount: 0,
        maxDailyAmount: 0,
        maxDay: null,
        daysWithData: 0
      };
    }

    // Получаем количество дней в месяце
    const daysInMonth = getDaysInMonth(month);
    
    // Инициализируем данные для каждого дня месяца
    const dayData = {};
    for (let i = 1; i <= daysInMonth; i++) {
      dayData[i] = 0;
    }

    // Группируем транзакции по дням
    let total = 0;
    let maxDaily = 0;
    let maxDayNumber = 0;
    let daysWithTransactions = 0;
    const daysWithValues = new Set();

    transactions.forEach(transaction => {
      const amount = parseFloat(transaction.amount);
      if (isNaN(amount)) return;

      // Использование специальной функции для извлечения дня из даты
      const day = extractDayFromDate(transaction.date);
      
      if (!dayData[day]) {
        dayData[day] = 0;
      }

      const originalCurrency = transaction.currency || 'UAH';
      // Конвертируем в предпочитаемую валюту
      const convertedAmount = convertCurrency(
        amount, 
        originalCurrency, 
        preferredCurrency, 
        exchangeRates
      );

      dayData[day] += convertedAmount;
      total += convertedAmount;
      
      daysWithValues.add(day);
      
      // Отслеживаем максимальную дневную сумму
      if (dayData[day] > maxDaily) {
        maxDaily = dayData[day];
        maxDayNumber = day;
      }
    });

    daysWithTransactions = daysWithValues.size;

    // Преобразуем в массив для графика
    const result = Object.entries(dayData).map(([day, value]) => ({
      day: parseInt(day),
      value: parseFloat(value.toFixed(2)),
      formattedValue: formatCurrency(value, preferredCurrency),
      hasTransactions: value > 0
    }));
    
    // Средняя сумма в день с транзакциями
    const avgAmount = daysWithTransactions > 0 ? total / daysWithTransactions : 0;

    return {
      dailyData: result,
      totalAmount: total,
      averageAmount: avgAmount,
      maxDailyAmount: maxDaily,
      maxDay: maxDayNumber,
      daysWithData: daysWithTransactions
    };
  }, [transactions, exchangeRates, preferredCurrency, month]);

  // Форматирование сумм
 const formattedTotalAmount = useMemo(() => {
    return safeFormatCurrency(totalAmount, preferredCurrency);
  }, [totalAmount, preferredCurrency, safeFormatCurrency]);

  const formattedAverageAmount = useMemo(() => {
    return safeFormatCurrency(averageAmount, preferredCurrency);
  }, [averageAmount, preferredCurrency, safeFormatCurrency]);

  const formattedMaxAmount = useMemo(() => {
    return safeFormatCurrency(maxDailyAmount, preferredCurrency);
  }, [maxDailyAmount, preferredCurrency, safeFormatCurrency]);
  
  
  // Определение названия графика в зависимости от типа
const chartTitle = type === 'expenses' 
  ? t('expenses_by_day_title')
  : type === 'income' 
    ? t('income_by_day_title') 
    : t('transactions_by_day_title');
  
  // Определение цвета в зависимости от типа
  const barColor = type === 'expenses' ? EXPENSE_COLOR : INCOME_COLOR;

  if (isLoading) {
    return (
      <ChartContainer>
        <ChartTitle>{chartTitle}</ChartTitle>
        <LoadingState>
        <LoadingIcon />
        <div>{t('chart_loading')}</div>
        </LoadingState>
      </ChartContainer>
    );
  }

  if (dailyData.length === 0 || daysWithData === 0) {
    return (
      <ChartContainer>
        <ChartTitle>{chartTitle}</ChartTitle>
        <EmptyState>
        <NoDataIcon />
        <div>{t('no_chart_data')}</div>
        </EmptyState>
      </ChartContainer>
    );
  }

  return (
    <ChartContainer>
      <ChartTitle>{chartTitle}</ChartTitle>
      
      <ResponsiveContainer width="100%" height={isSmallScreen ? 300 : 400}>
        <BarChart
          data={dailyData}
          margin={{
            top: 20,
            right: 30,
            left: isSmallScreen ? 0 : 20,
            bottom: 20,
          }}
          barCategoryGap={isSmallScreen ? 2 : 4}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis 
            dataKey="day" 
            stroke="#8c96a5" 
            tick={{ fontSize: isSmallScreen ? 10 : 12 }}
            tickCount={isSmallScreen ? 10 : 31}
            interval={isSmallScreen ? 2 : 1}
            label={isSmallScreen ? null : { 
            value: t('day_of_month_label'), 
            position: 'insideBottom', 
            offset: -10,
            fontSize: 12,
            fill: '#8c96a5'
        }}
        />
          
          <YAxis 
            stroke="#8c96a5" 
            tick={{ fontSize: isSmallScreen ? 10 : 12 }}
            tickFormatter={(value) => {
              if (value >= 1000) {
                return `${(value / 1000).toFixed(1)}k`;
              }
              return value;
            }}
            width={isSmallScreen ? 40 : 60}
            label={isSmallScreen ? null : { 
              value: preferredCurrency, 
              angle: -90, 
              position: 'insideLeft',
              fontSize: 12,
              fill: '#8c96a5' 
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            formatter={(value) => <span style={{ color: '#6c7d95', fontSize: 12 }}>
                {t(value === 'expenses' ? 'expenses_legend' : 'income_legend')}
            </span>}
            />
          <Bar 
            dataKey="value" 
            name={type === 'expenses' ? t('expenses') : t('income')}
            fill={barColor} 
            radius={[4, 4, 0, 0]} 
            barSize={isSmallScreen ? 10 : 20}
            animationDuration={1000}
          >
            {dailyData.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={entry.hasTransactions ? barColor : EMPTY_COLOR} 
                opacity={entry.hasTransactions ? 1 : 0}
              />
            ))}
          </Bar>
          <ReferenceLine
            y={averageAmount}
            stroke={AVERAGE_COLOR}
            strokeWidth={2}
            strokeDasharray="3 3"
            label={{
                value: t('average_line'),
                position: 'right',
                fill: AVERAGE_COLOR,
                fontSize: isSmallScreen ? 10 : 12
            }}
            />

        {maxDay && (
        <ReferenceLine
            x={maxDay}
            stroke="#4A6FA1"
            strokeWidth={1}
            label={{
            value: t('max_marker'),
            position: 'top',
            fill: '#4A6FA1',
            fontSize: isSmallScreen ? 10 : 12
            }}
        />
        )}
        </BarChart>
      </ResponsiveContainer>
      
      <StatsContainer>
        <StatItem>
            <div className="stat-label">{t('total_amount')}</div>
            <div className="stat-value">{formattedTotalAmount}</div>
        </StatItem>
        
        <StatItem>
            <div className="stat-label">{t('daily_avg')}</div>
            <div className="stat-value">{formattedAverageAmount}</div>
        </StatItem>
        
        <StatItem $color={barColor}>
            <div className="stat-label">{t('max_day_stat')}</div>
            <div className="stat-value">{maxDay ? `${maxDay} (${formattedMaxAmount})` : '-'}</div>
        </StatItem>
        
        <StatItem>
            <div className="stat-label">{t('days_with_trans')}</div>
            <div className="stat-value">{daysWithData}</div>
        </StatItem>
        </StatsContainer>
        
    </ChartContainer>
  );
};

export default ChartComponent;